import React, { type MouseEvent, useCallback } from 'react';
import { Anchor, xcss } from '@atlaskit/primitives';
import ArrowUpRightIcon from '@atlaskit/icon/core/arrow-up-right';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { useIsEmbedMode } from '@atlassian/jira-issue-view-embed-mode/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import messages from './messages.tsx';

type Props = {
	issueKey: IssueKey;
	onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
};
export const OpenIssueLink = ({ issueKey, onClick }: Props) => {
	const { formatMessage } = useIntl();
	const isEmbedMode = fg('jira_nin_smart_link')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIsEmbedMode()
		: false;
	const handleClick = useCallback(
		(e: MouseEvent<HTMLAnchorElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'tableIssueLink');
			onClick?.(e);
		},
		[onClick],
	);
	return (
		<Tooltip content={formatMessage(messages.openTooltip)}>
			{(triggerProps) => (
				<Anchor
					{...triggerProps}
					aria-label={formatMessage(messages.openLabel, { issueKey })}
					href={`/browse/${issueKey}`}
					onClick={handleClick}
					xcss={fg('jira_list_merged_column_gate') ? buttonStyles : buttonStylesOld}
					// Cannot provide CSS variable as a compile time value due to various type constraints between
					// primitives + compiled.
					// eslint-disable-next-line jira/react/no-style-attribute,@atlaskit/ui-styling-standard/enforce-style-prop
					style={
						fg('jira_list_merged_column_gate')
							? buttonOpacityVariableStyle
							: buttonOpacityVariableStyleOld
					}
					{...(isEmbedMode && { target: '_blank', rel: 'noopener' })}
				>
					<ArrowUpRightIcon label="" />
				</Anchor>
			)}
		</Tooltip>
	);
};

const buttonOpacityVariableStyle = {
	display: 'var(--open-button-display, "none")',
};

/**
 * @deprecated remove when jira_list_merged_column_gate is removed
 */
const buttonOpacityVariableStyleOld = {
	opacity: 'var(--open-button-opacity, 0)',
};

const buttonStyles = xcss({
	alignItems: 'center',
	borderRadius: 'border.radius',
	borderStyle: 'none',
	borderColor: 'color.border',
	borderWidth: '0',
	color: 'color.text',
	display: 'inline-flex',
	height: '24px',
	justifyContent: 'center',
	marginLeft: 'auto',
	paddingTop: 'space.050',
	paddingRight: 'space.050',
	paddingBottom: 'space.050',
	paddingLeft: 'space.050',
	width: '24px',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
		color: 'color.text',
	},
	':focus': {
		color: 'color.text',
	},
	':active': {
		color: 'color.text',
	},
});

/**
 * @deprecated remove when jira_list_merged_column_gate is removed
 */
const buttonStylesOld = xcss({
	alignItems: 'center',
	borderRadius: 'border.radius',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderWidth: 'border.width',
	color: 'color.text',
	display: 'inline-flex',
	height: '24px',
	justifyContent: 'center',
	marginLeft: 'auto',
	paddingTop: 'space.050',
	paddingRight: 'space.050',
	paddingBottom: 'space.050',
	paddingLeft: 'space.050',
	width: '24px',
	':hover': {
		backgroundColor: 'elevation.surface.hovered',
		color: 'color.text',
	},
	':focus': {
		color: 'color.text',
	},
	':active': {
		color: 'color.text',
	},
});
